import * as React from 'react'

import { RESET_DATA, UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'

export type ActionTypes = 'RESET_DATA' | 'UPDATE_DATA'

export interface ResetDataAction {
  type: 'RESET_DATA'
}

export interface UpdateDataAction {
  type: 'UPDATE_DATA'
  payload: Omit<Partial<State>, 'insightsDispatch'>
}

export type Actions = ResetDataAction | UpdateDataAction

export interface SupplementsByTaxonomy {
  taxonomyId: number
  total: number
  taxonomyName: string
  legalEntityId: number
  legalEntityName: string
  legalEntityCurrency: string
}

export interface Params {
  policyPeriodId?: number
  previousPolicyPeriodId?: number
  triggerFetch?: boolean
}

export interface TotalByCurrency {
  currency: string
  total: number
}

export interface State {
  totalByCurrency: TotalByCurrency[]
  legalEntityCount: number
  currentPeriodSupplementCount: number
  currentPeriodDraftSupplementCount: number
  previousPeriodSupplementCount: number
  totalByTaxonomy: SupplementsByTaxonomy[]
  totalByEmployee: Record<number, number>
  previousPeriodTotalByCurrency: TotalByCurrency[]
  previousPeriodTotalByTaxonomy: SupplementsByTaxonomy[]
  previousPeriodTotalByEmployee: Record<number, number>
  insightsDispatch: React.Dispatch<Actions>
}

const initialState: State = {
  legalEntityCount: 0,
  currentPeriodSupplementCount: 0,
  currentPeriodDraftSupplementCount: 0,
  previousPeriodSupplementCount: 0,
  totalByCurrency: [],
  totalByEmployee: {},
  totalByTaxonomy: [],
  previousPeriodTotalByCurrency: [],
  previousPeriodTotalByEmployee: {},
  previousPeriodTotalByTaxonomy: [],
  insightsDispatch: () => {
    /* no-op */
  },
}

const supplementsInsightsContext: React.Context<State> = React.createContext(initialState)

const SupplementsInsightsProvider = supplementsInsightsContext.Provider

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case UPDATE_DATA:
      return { ...state, ...action.payload }
    case RESET_DATA:
      return initialState
    default:
      throw new Error()
  }
}

interface Props {
  children: React.ReactNode
}

export const SupplementsInsightsContextProvider = ({ children }: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState)

  return (
    <SupplementsInsightsProvider value={{ ...state, insightsDispatch: dispatch }}>
      {children}
    </SupplementsInsightsProvider>
  )
}

export const useSupplementsInsights = () => {
  const supplementsInsights = React.useContext(supplementsInsightsContext)

  if (!supplementsInsights) {
    throw new Error(
      'useSupplementsInsights hook must be used within a SupplementsInsightsContextProvider'
    )
  }

  return supplementsInsights
}
